import { EventEmitter, Injectable, inject } from '@angular/core';
import {
  AgendaEvent,
  AgendaEventType,
  CalendarEventInfo,
  CalendarEventsResponse,
} from '@cropx-common/models/CalendarEventsModel';
import { UserPropsService } from '@cropx-common/services/local-storage/UserPropsService';
import { UnitFormatterService } from '@cropx-common/services/local-storage/measurement.service';
import { EventHelper } from '@cropx-web/screens/field/field-agenda/event-utils';
import { ApiGatewayService } from '@cropx-web/services/api-gateway/ApiGatewayService';
import {
  Record,
  RecordsResponse,
} from '@cropx-web/services/api-gateway/CropRecordingApiService';
import { TranslateService } from '@ngx-translate/core';
import {
  Action,
  Selector,
  State,
  StateContext,
  createPropertySelectors,
  createSelector,
} from '@ngxs/store';
import { patch, removeItem } from '@ngxs/store/operators';
import { EMPTY, Observable, catchError, first, tap } from 'rxjs';

import { ApiService } from '../../../services/network/ApiService';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace Agenda {
  const type = 'agenda';

  export class SetActiveField {
    static readonly type = `[${type}] SetActiveField`;

    constructor(public fieldId: string) {}
  }

  export class GetAgendaForField {
    static readonly type = `[${type}] GetAgendaForField`;
  }

  export class FetchAgendaForField {
    static readonly type = `[${type}] FetchAgendaForField`;

    constructor(public fieldId: string) {}
  }

  export class SetEvents {
    static readonly type = `[${type}] SetEvents`;

    constructor(public events: AgendaEvent[]) {}
  }

  export class DeleteEvent {
    static readonly type = `[${type}] deleteEvent`;

    constructor(public eventId: number) {}
  }

  export class FetchRecords {
    static readonly type = `[${type}] FetchRecords`;

    constructor(
      public farmUuid: string,
      public cropZones: string[],
      public from: number,
      public to: number,
    ) {}
  }
}

export interface AgendaStateModel {
  events: AgendaEvent[];
  activeFieldId: string;
  loading: boolean;
  records: Record[];
  recordsLoading: boolean;
}

@State<AgendaStateModel>({
  name: 'agenda',
  defaults: {
    events: [],
    activeFieldId: '',
    loading: false,
    records: [],
    recordsLoading: false,
  },
})
@Injectable()
export class AgendaState {
  private oldAPi = inject(ApiService);
  private api = inject(ApiGatewayService);
  private translation = inject(TranslateService);
  private userPropsService = inject(UserPropsService);
  private unitFormat = inject(UnitFormatterService);

  @Action(Agenda.SetActiveField)
  setActiveField(
    ctx: StateContext<AgendaStateModel>,
    action: Agenda.SetActiveField,
  ): void {
    ctx.patchState({ activeFieldId: action.fieldId });
  }

  @Action(Agenda.GetAgendaForField)
  getAgendaForField(ctx: StateContext<AgendaStateModel>): void {
    ctx.patchState({ loading: true });

    const { activeFieldId } = ctx.getState();
    const timestamp = Date.now();
    const period = 'MONTH';
    const margin = 12;

    const resp = new EventEmitter<CalendarEventsResponse>();
    const err = new EventEmitter<any>();

    this.oldAPi.fieldApi.getFieldCalendarEvents(
      activeFieldId,
      timestamp,
      period,
      resp,
      err,
      margin,
    );

    resp.subscribe((response) => {
      const events: AgendaEvent[] = [];

      response.content.events.forEach((em) =>
        em.dayEvents.forEach((ed) => {
          ed.events.forEach((e) => {
            if (e.type === AgendaEventType.Custom) {
              e.type = e.customCalenderEvent.additionals.name;
            }

            const displayValue = EventHelper.getEventDisplayValue(
              e,
              this.userPropsService,
              this.unitFormat,
            );

            const description = EventHelper.getEventName(
              e.type,
              this.translation,
              e.objectNature,
            );

            events.push({
              ...e,
              description: description,
              displayValue: displayValue,
            });
          });
        }),
      );
      ctx.dispatch(new Agenda.SetEvents(events));
      ctx.patchState({ loading: false });
    });
  }

  @Action(Agenda.FetchAgendaForField)
  fetchAgendaForField(
    ctx: StateContext<AgendaStateModel>,
    action: Agenda.FetchAgendaForField,
  ): void {
    ctx.patchState({ loading: true });

    const timestamp = Date.now();
    const period = 'MONTH';
    const margin = 12;

    const resp = new EventEmitter<CalendarEventsResponse>();
    const err = new EventEmitter<any>();

    this.oldAPi.fieldApi.getFieldCalendarEvents(
      action.fieldId,
      timestamp,
      period,
      resp,
      err,
      margin,
    );

    resp.subscribe((response) => {
      const events: AgendaEvent[] = [];

      response.content.events.forEach((em) =>
        em.dayEvents.forEach((ed) => {
          ed.events.forEach((e) => {
            if (e.type === AgendaEventType.Custom) {
              e.type = e.customCalenderEvent.additionals.name;
            }
            events.push({
              ...e,
              description: EventHelper.getEventName(
                e.type,
                this.translation,
                e.objectNature,
              ),
              displayValue: EventHelper.getEventDisplayValue(
                e,
                this.userPropsService,
                this.unitFormat,
              ),
            });
          });
        }),
      );

      ctx.dispatch(new Agenda.SetEvents(events));
      ctx.patchState({ loading: false });
    });
  }

  @Action(Agenda.DeleteEvent)
  deleteAgendaEvent(
    ctx: StateContext<AgendaStateModel>,
    action: Agenda.DeleteEvent,
  ): void {
    ctx.patchState({ loading: true });

    const { activeFieldId } = ctx.getState();

    const resp = new EventEmitter<CalendarEventsResponse>();
    const err = new EventEmitter<any>();

    this.oldAPi.fieldApi.deleteCalendarEvent(
      activeFieldId,
      action.eventId,
      resp,
      err,
    );

    resp.pipe(first()).subscribe(() => {
      ctx.setState(
        patch({
          events: removeItem<AgendaEvent>((e) => e.eventId === action.eventId),
          loading: false,
        }),
      );
    });

    err.pipe(first()).subscribe(() => {
      ctx.patchState({
        loading: false,
      });
    });
  }

  @Action(Agenda.SetEvents)
  setEvents(
    ctx: StateContext<AgendaStateModel>,
    action: Agenda.SetEvents,
  ): void {
    ctx.patchState({ events: action.events });
  }

  @Action(Agenda.FetchRecords)
  fetchRecords(
    ctx: StateContext<AgendaStateModel>,
    action: Agenda.FetchRecords,
  ): Observable<any> {
    const { farmUuid, cropZones, from, to } = action;
    ctx.patchState({
      records: [],
      recordsLoading: true,
    });

    return this.api.cropRecording
      .getRecords(farmUuid, cropZones, from, to)
      .pipe(
        tap((resp: RecordsResponse) => {
          ctx.patchState({
            records: resp.data,
            recordsLoading: false,
          });
        }),
        catchError(() => {
          ctx.patchState({
            records: [],
            recordsLoading: false,
          });

          return EMPTY;
        }),
      );
  }
}

export class AgendaSelectors {
  static slices = createPropertySelectors<AgendaStateModel>(AgendaState);

  @Selector([AgendaSelectors.slices.events])
  static eventsFiltered(events: AgendaEvent[]): AgendaEvent[] {
    const unusedEvents = [
      AgendaEventType.ETService,
      AgendaEventType.ETServiceForecast,
      AgendaEventType.IrrigationRecommendation,
      // Is disabled on mobile, but not on web currently
      // AgendaEventType.RecommendationDate,
    ];

    events = events.filter((e) => !unusedEvents.includes(e.type));

    return events;
  }

  static eventsExcludedByTypes(
    types: AgendaEventType[],
  ): (events: CalendarEventInfo[]) => CalendarEventInfo[] {
    return createSelector(
      [AgendaSelectors.eventsFiltered],
      (events: CalendarEventInfo[]) => {
        return events.filter((e) => !types.includes(e.type));
      },
    );
  }

  static eventsByTypes(
    types: AgendaEventType[],
  ): (events: CalendarEventInfo[]) => AgendaEvent[] {
    return createSelector(
      [AgendaSelectors.eventsFiltered],
      (events: AgendaEvent[]) => {
        return events.filter((e) => types.includes(e.type));
      },
    );
  }

  static getTasksEventsByTypes(
    types: AgendaEventType[],
  ): (events: AgendaEvent[]) => TaskEvent[] {
    return createSelector(
      [AgendaSelectors.eventsFiltered],
      (events: TaskEvent[]) => {
        const filteredEvents = events.filter((e) => types.includes(e.type));
        return AgendaSelectors.getTasksEvents(filteredEvents);
      },
    );
  }

  private static getTasksEvents(events: AgendaEvent[]): TaskEvent[] {
    const eventTime = new Date(new Date().setUTCHours(0, 0, 0, 0)).getTime();
    const sortedEvents = this.getSortedTaskEvents(events);

    const hasFutureEvent = (element: CalendarEventInfo) =>
      element.eventTime >= eventTime;
    const index = sortedEvents.findIndex(hasFutureEvent);

    let filteredEvents = new Array<TaskEvent>();
    let moreEvents = new Array<TaskEvent>();
    if (index > -1) {
      if (sortedEvents.length > 5) {
        filteredEvents = sortedEvents.slice(index, index + 5);
        moreEvents = sortedEvents.slice(index + 5, sortedEvents.length);
      }
      if (sortedEvents.length <= 5) {
        const beginSlice = index - (5 - sortedEvents.length);
        const endSlice = index;

        const pastEvents = sortedEvents.slice(beginSlice, endSlice);
        filteredEvents = [...pastEvents, ...sortedEvents];
      }
    } else {
      filteredEvents = sortedEvents.slice(-5);
      moreEvents = sortedEvents.slice(0, -5);
    }

    const mainEvents = filteredEvents.map((e) => {
      return {
        ...e,
        mainEvent: true,
      };
    });
    const collapsedEvents = moreEvents.map((e) => {
      return {
        ...e,
        mainEvent: false,
      };
    });
    return [...mainEvents, ...collapsedEvents];
  }

  private static getSortedTaskEvents(events: AgendaEvent[]): TaskEvent[] {
    const sortedEvents = [...events];
    sortedEvents.sort((a, b) => (a.eventTime < b.eventTime ? 1 : -1));
    const taskEvents: TaskEvent[] = sortedEvents.map((event) => {
      return { ...event, mainEvent: true };
    });
    return taskEvents;
  }
}

export type TaskEvent = {
  mainEvent: boolean;
} & AgendaEvent;
